import { useEffect, useState } from 'react';
import {
  endImpersonation,
  readImpersonationSession,
  type ImpersonationSession,
} from '@/screens/RAdmin/Tenants/ImpersonateModal';

/**
 * Persistent red banner shown while an impersonation session is
 * active. Reads the session from sessionStorage on mount and listens
 * for the 'pdm:impersonation-changed' event the modal fires after a
 * successful mint. Auto-clears once expiresAt passes.
 */
export function ImpersonationBanner() {
  const [session, setSession] = useState<ImpersonationSession | null>(() =>
    readImpersonationSession(),
  );
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const onChange = () => setSession(readImpersonationSession());
    window.addEventListener('pdm:impersonation-changed', onChange);
    return () => window.removeEventListener('pdm:impersonation-changed', onChange);
  }, []);

  useEffect(() => {
    if (!session) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [session]);

  if (!session) return null;
  const expires = new Date(session.expiresAt).getTime();
  if (Number.isFinite(expires) && expires <= now) {
    // Lazy auto-end when the timer runs out.
    return null;
  }
  const remainingMs = Math.max(0, expires - now);
  const mins = Math.floor(remainingMs / 60_000);
  const secs = Math.floor((remainingMs % 60_000) / 1000);
  const remaining = `${mins.toString().padStart(2, '0')}:${secs
    .toString()
    .padStart(2, '0')}`;

  return (
    <div
      role="status"
      className="flex items-center justify-between gap-sp-3 border-b border-critical bg-critical-bg px-sp-4 py-sp-2 text-fs-meta text-critical"
    >
      <span>
        Impersonating <strong className="font-medium">{session.tenantName}</strong>
        {' · '}
        <span className="font-mono">{session.mode}</span>
        {' · expires in '}
        <span className="font-mono">{remaining}</span>
      </span>
      <button
        type="button"
        onClick={endImpersonation}
        className="font-medium underline hover:text-gray-900"
      >
        End session →
      </button>
    </div>
  );
}
